/**
 * Neeko, Blending In: "If Neeko is in your deck, choose 3 different legends in
 * addition to your starting legend. (Different means none match names.)"
 *
 * The chosen legends never change construction domains, runes, or the Chosen
 * Champion. Shared by the deck builder and the publish API.
 */

import { legendIdentityFromRef } from './legendIdentity.js';

export const CHOSEN_LEGEND_COUNT = 3;

const ENABLER_PRINT_KEYS = new Set(['neeko-blending in']);

/** Print title with variant suffixes dropped, so alt arts and reprints match. */
function printKey(name) {
  let s = String(name ?? '').toLowerCase().trim();
  let prev;
  do {
    prev = s;
    s = s.replace(/\s*[（(][^()（）]*[）)]\s*$/, '').trim();
  } while (s !== prev);
  return s
    .replace(/[\u2010-\u2015\u2212]/g, '-')
    .replace(/\s*[,-]\s*/g, '-')
    .replace(/\s+/g, ' ');
}

function isLegendCard(card) {
  return String(card?.cardType ?? '').toLowerCase() === 'legend';
}

/** The card whose presence requires chosen legends. */
export function isChosenLegendsEnabler(card) {
  if (!card || String(card.cardType ?? '').toLowerCase() !== 'unit') return false;
  return ENABLER_PRINT_KEYS.has(printKey(card.name));
}

/** Chosen Champion, main deck, or sideboard. The considered pile is not in the deck. */
export function deckHasChosenLegendsEnabler(deck) {
  if (isChosenLegendsEnabler(deck?.champion)) return true;
  const inList = (list) =>
    (Array.isArray(list) ? list : []).some((card) => (card?.qty ?? 0) > 0 && isChosenLegendsEnabler(card));
  return inList(deck?.mainDeck) || inList(deck?.sideboard);
}

/**
 * Why `candidate` cannot be added as a chosen legend, or null when it can.
 * Reasons: `not-legend`, `no-enabler`, `full`, `starting-legend`, `already-chosen`.
 */
export function chosenLegendRejection(deck, candidate) {
  if (!isLegendCard(candidate)) return 'not-legend';
  if (!deckHasChosenLegendsEnabler(deck)) return 'no-enabler';
  const chosen = Array.isArray(deck?.chosenLegends) ? deck.chosenLegends : [];
  const identity = legendIdentityFromRef(candidate);
  if (identity && deck?.legend && legendIdentityFromRef(deck.legend) === identity) {
    return 'starting-legend';
  }
  if (identity && chosen.some((card) => legendIdentityFromRef(card) === identity)) {
    return 'already-chosen';
  }
  if (chosen.length >= CHOSEN_LEGEND_COUNT) return 'full';
  return null;
}

/** Construction problems with the chosen legends, as user-facing messages. */
export function chosenLegendProblems(deck) {
  const chosen = Array.isArray(deck?.chosenLegends) ? deck.chosenLegends : [];
  if (!deckHasChosenLegendsEnabler(deck)) {
    return chosen.length > 0
      ? ['Chosen Legends only apply while Neeko, Blending In is in the deck.']
      : [];
  }

  const problems = [];
  if (chosen.length !== CHOSEN_LEGEND_COUNT) {
    problems.push(
      `Neeko needs exactly ${CHOSEN_LEGEND_COUNT} chosen Legends besides your starting Legend (have ${chosen.length}).`,
    );
  }

  const startingIdentity = deck?.legend ? legendIdentityFromRef(deck.legend) : '';
  const seen = new Set();
  for (const card of chosen) {
    const name = card?.name ?? 'Unknown card';
    if (!isLegendCard(card)) {
      problems.push(`"${name}" is not a Legend, so Neeko cannot choose it.`);
      continue;
    }
    const identity = legendIdentityFromRef(card);
    if (!identity) continue;
    if (identity === startingIdentity) {
      problems.push(`"${name}" has the same name as your starting Legend. Neeko's Legends must all be different.`);
    } else if (seen.has(identity)) {
      problems.push(`"${name}" has the same name as another chosen Legend. Neeko's Legends must all be different.`);
    }
    seen.add(identity);
  }
  return problems;
}
